import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { RadioButton } from "primereact/radiobutton";
import { useEffect, useState } from "react";

import { SectionTitle, Surface } from "./Surface";

/**
 * WHAT THE PHYSICIAN COPAY READS IS NOT CONFIGURABLE.
 *
 * Since the `Professional (Physician) Visit - Outpatient` service line was retired on 2026-09-09,
 * the surgeon lines inside `Surgical` are the only place in a pVerify response where a physician
 * copay a facility can bill appears. So the configuration is on or off; what it reads is SHOWN, not
 * chosen — a dropdown here would offer a choice that has one member.
 */
const WHAT_IT_READS = [
  {
    when: "Every estimate",
    reads: "Surgical: the surgeon's own copay line (line type: Physician line)",
  },
  {
    when: "MRI/CT estimates",
    reads: "MRI/CAT Scan: the physician's line (line type: MRI physician line)",
  },
];

interface IPhysicianCopayControlProps {
  billed: boolean;
  disabled?: boolean;
  /**
   * CLE-2802 — true while `BENEFIT_SELECTION_RULES=ON`, when this box sits at the top of
   * "3 · Physician copay rules" and the rules under it decide which lines are read — so it points at
   * them instead of restating a built-in default those rules may have changed.
   */
  rulesOn?: boolean;
  onChange: (billed: boolean) => void;
}

export function PhysicianCopayControl({
  billed,
  disabled,
  rulesOn = false,
  onChange,
}: IPhysicianCopayControlProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(billed);

  useEffect(() => {
    if (open) setDraft(billed);
  }, [open, billed]);

  const whenCellBody = (row: (typeof WHAT_IT_READS)[number]) => (
    <span className="font-medium text-sm">{row.when}</span>
  );

  const readsCellBody = (row: (typeof WHAT_IT_READS)[number]) => (
    <span className="text-sm">{row.reads}</span>
  );

  return (
    <div
      // CLE-2802 — at the top of "3 · Physician copay rules" the step heading already names it, so no
      // box and no title of its own: one line of status with the button right beside it, not across
      // the page from it.
      className={
        rulesOn
          ? "flex align-items-center gap-3"
          : "flex align-items-center justify-content-between gap-3 p-3 border-round border-1 border-gray-200"
      }
      data-testid="benefitSelection.physicianCopay"
    >
      <div className="flex flex-column gap-1">
        {!rulesOn && <SectionTitle>Physician copay</SectionTitle>}
        <span className="text-sm text-color-secondary">
          {billed
            ? "Billed at this account — the engine answers a physician copay."
            : "Not billed at this account — the engine answers no physician copay."}
        </span>
        {/*
          Shown only while billed. Under "Not billed … answers no physician copay" a sentence
          starting "Reads Surgical" contradicted the line above it (Mike, 2026-09-14).
        */}
        {billed && (
          <span
            className="text-xs text-color-secondary"
            data-testid="benefitSelection.physicianCopay.reads"
          >
            {rulesOn
              ? "The rules below choose which of the surgeon's lines it reads."
              : "Reads Surgical, the surgeon's own copay lines (MRI/CAT Scan on MRI/CT estimates)."}
          </span>
        )}
      </div>

      <div className="flex align-items-center gap-3">
        {/*
          ⚠️ NO YES/NO PILL (review round 3). The sentence beside it already says whether this
          account bills a physician copay; the button now says what pressing it would do, which is
          the only thing the pill was not telling anybody.
        */}
        {/*
          CLE-2802 — with selection rules on, the button just flips the switch: the rules under it
          appear or disappear at once, and the card's Save is where the change is kept. The dialog
          stays for the old card, where it also explains what the copay reads.
        */}
        <Button
          type="button"
          label={billed ? "Disable" : "Enable"}
          className="p-button-outlined p-button-sm"
          disabled={disabled}
          data-testid="benefitSelection.physicianCopay.change"
          onClick={() => (rulesOn ? onChange(!billed) : setOpen(true))}
        />
      </div>

      <Dialog
        header="Physician copay"
        visible={open}
        onHide={() => setOpen(false)}
        style={{ width: "min(40rem, 95vw)" }}
        data-testid="benefitSelection.physicianCopayDialog"
        footer={
          <div className="flex justify-content-end gap-2">
            <Button
              type="button"
              label="Cancel"
              className="p-button-text p-button-sm"
              data-testid="benefitSelection.physicianCopayDialog.cancel"
              onClick={() => setOpen(false)}
            />
            <Button
              className="p-button-sm"
              type="button"
              label="Apply"
              data-testid="benefitSelection.physicianCopayDialog.save"
              onClick={() => {
                onChange(draft);
                setOpen(false);
              }}
            />
          </div>
        }
      >
        <Surface className="flex flex-column gap-4">
          <div className="flex flex-column gap-2">
            <span className="font-medium text-sm">Billed at this account</span>
            <div className="flex gap-4">
              <span className="flex align-items-center gap-2">
                <RadioButton
                  inputId="physicianCopayYes"
                  checked={draft}
                  onChange={() => setDraft(true)}
                />
                <label htmlFor="physicianCopayYes" className="text-sm">
                  Yes
                </label>
              </span>
              <span className="flex align-items-center gap-2">
                <RadioButton
                  inputId="physicianCopayNo"
                  checked={!draft}
                  onChange={() => setDraft(false)}
                />
                <label htmlFor="physicianCopayNo" className="text-sm">
                  No
                </label>
              </span>
            </div>
          </div>

          <div className="flex flex-column gap-2">
            <span className="font-medium text-sm">What it reads</span>
            {rulesOn ? (
              <span
                className="text-sm text-color-secondary"
                data-testid="benefitSelection.physicianCopayDialog.whatItReads"
              >
                Set by the rules in this step, shown while the copay is billed.
              </span>
            ) : (
              <DataTable
                value={WHAT_IT_READS}
                dataKey="when"
                data-testid="benefitSelection.physicianCopayDialog.whatItReads"
              >
                <Column
                  header=""
                  body={whenCellBody}
                  style={{ width: "12rem" }}
                />
                <Column header="" body={readsCellBody} />
              </DataTable>
            )}
          </div>
        </Surface>
      </Dialog>
    </div>
  );
}
